import { useEffect, useRef, useState } from "react";
import Script from "next/script";
import { Typography } from "@mui/material";
import { Box, CircularProgress } from "@mui/material";
import ConfirmModal from "../ConfirmModal";
import { useTranslation } from "react-i18next";
import { useTourBookingContext } from "@/containers/TourBookingByCustomer/providers/useTourBookingContext";

export type PayPalPaymentModalProps = {
  paypalRef?: any;
  isOpen: boolean;
  renderPaypalButtons: () => Promise<unknown> | unknown;
  onClose: () => void;
  paypalOrderId: string | undefined;
  paypalPaymentApproved: boolean;
}

export default function PayPalPaymentModal({ paypalRef, isOpen, renderPaypalButtons, onClose, paypalOrderId, paypalPaymentApproved }: PayPalPaymentModalProps) {
  const { t } = useTranslation('common');

  const { finishRenderPaypalButtons, setFinishRenderPaypalButtons } = useTourBookingContext();
  const [paypalSdkReady, setPaypalSdkReady] = useState(false);
  const [paypalRenderError, setPaypalRenderError] = useState(false);
  const renderStartedRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      renderStartedRef.current = false;
      return;
    }

    if (!paypalSdkReady || !paypalRef?.current || renderStartedRef.current) {
      return;
    }

    renderStartedRef.current = true;
    setPaypalRenderError(false);
    setFinishRenderPaypalButtons?.(false);

    const paypalElement = paypalRef.current as HTMLElement;
    let isActive = true;
    let observer: MutationObserver | undefined;

    const markButtonsAsRendered = () => {
      if (!isActive || !paypalElement.querySelector('iframe')) {
        return false;
      }

      setPaypalRenderError(false);
      setFinishRenderPaypalButtons?.(true);
      observer?.disconnect();
      return true;
    };

    observer = new MutationObserver(markButtonsAsRendered);
    observer.observe(paypalElement, { childList: true, subtree: true });

    Promise.resolve()
      .then(() => renderPaypalButtons())
      .then(() => {
        if (isActive) {
          setPaypalRenderError(false);
          setFinishRenderPaypalButtons?.(true);
        }
      })
      .catch((error) => {
        if (!isActive) return;

        console.error('PayPal buttons failed to render', error);
        setPaypalRenderError(true);
        setFinishRenderPaypalButtons?.(true);
      });

    markButtonsAsRendered();

    return () => {
      isActive = false;
      observer?.disconnect();
    };
  }, [isOpen, paypalSdkReady, paypalRef, renderPaypalButtons, setFinishRenderPaypalButtons]);

  function handleClose() {
    renderStartedRef.current = false;
    setPaypalRenderError(false);
    onClose();
    if (setFinishRenderPaypalButtons) {
      setFinishRenderPaypalButtons(false);
    }
  }


  return <>
    <Script
      id="paypal-script"
      strategy="afterInteractive"
      src={`https://www.paypal.com/sdk/js?client-id=${process.env.NEXT_PUBLIC_PAYPAL_CLIENT_ID}&currency=USD`}
      onReady={() => {
        const isReady = Boolean(window.paypal);
        setPaypalSdkReady(isReady);

        if (!isReady) {
          setPaypalRenderError(true);
          setFinishRenderPaypalButtons?.(true);
        }
      }}
      onError={() => {
        setPaypalSdkReady(false);
        setPaypalRenderError(true);
        setFinishRenderPaypalButtons?.(true);
      }}
    />
    <ConfirmModal
      isOpen={isOpen}
      title={t('paypalPayment')}
      noAction={handleClose}
      sx={{
        height: paypalOrderId ? '80dvh' : '360px',
        duration: 300,
        width: '500px',
        maxWidth: '95dvw',
        transition: 'all 0.3s ease-in-out',
        overflowY: 'scroll',
      }}
      modalProps={{
        open: isOpen,
        disableEscapeKeyDown: true,
        disableAutoFocus: true,
        disableEnforceFocus: true,
        disableRestoreFocus: true,
        onBackdropClick: (e: any) => e.stopPropagation(),
        children: <></>,
      }}
    >
      <Box
        ref={paypalRef}
        width="100%"
        sx={{
          display: paypalPaymentApproved ? 'none' : 'flex',
          justifyItems: 'center',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      />
      {paypalRenderError && (
        <Typography role="alert" color="error" textAlign="center">
          {t('paymentFailed')}
        </Typography>
      )}
      {!finishRenderPaypalButtons && (
        <Box
          position="absolute"
          alignItems="center"
          justifyContent="center"
          gap={2}
          sx={{
            zIndex: 999,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'fit-content',
            height: 'fit-content',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            padding: '8px',
            borderRadius: '999px',
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {paypalPaymentApproved && (
        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          <CircularProgress />
          <Typography>
            {t('common:bookingConfirmed')}
          </Typography>
        </Box>
      )}
    </ConfirmModal>
  </>
}
